import React from 'react';
import { Paper, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { Translate } from 'react-localize-redux';

// SHER-353: extensible set of result filters shown beside the search. Add new flags here
// (and a control in this panel + the place that applies them) as more filters land.
export type SearchFilters = {
    appointmentToday: boolean;
};

interface SearchFiltersPanelProps {
    filters: SearchFilters;
    setFilters: (filters: SearchFilters) => void;
    // OUTPATIENTS functionality gates the "appointment today" filter.
    hasOutpatients: boolean;
    // DECEASED_WORKFLOW gates the "include deceased" filter. Its checked state lives in a
    // separate piece of container state (it feeds the search query + the results table), so
    // it comes through as its own value/setter rather than as a flag inside the filters bag.
    hasDeceasedWorkflow: boolean;
    includeDeceased: boolean;
    setIncludeDeceased: (value: boolean) => void;
}

export const SearchFiltersPanel: React.FC<SearchFiltersPanelProps> = ({ filters, setFilters, hasOutpatients, hasDeceasedWorkflow, includeDeceased, setIncludeDeceased }) => {
    // No filters available for this tenant → render nothing at all (no panel, no heading).
    // Each filter is gated by its own functionality; the panel shows up as soon as any of
    // them applies. Add more conditions here as filters land.
    if (!hasOutpatients && !hasDeceasedWorkflow) return null;

    return (
        // Sits in a right-hand sidebar column: only a top margin so it lines up with the
        // search form's first card; horizontal margins are left to the grid gutter so the
        // panel fills its column instead of floating narrow inside it.
        <Paper style={{ padding: '1rem', marginTop: '1rem' }}>
            <Typography variant="h6" gutterBottom>
                <Translate id="pages.hospital.search-patient.filters.title" />
            </Typography>
            {hasOutpatients && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={filters.appointmentToday}
                            onChange={(e) => setFilters({ ...filters, appointmentToday: e.target.checked })}
                            size="small"
                            data-testid="filter-appointment-today"
                        />
                    }
                    label={<Translate id="pages.hospital.search-patient.filters.appointment-today" />}
                />
            )}
            {hasDeceasedWorkflow && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeDeceased}
                            onChange={(e) => setIncludeDeceased(e.target.checked)}
                            size="small"
                            data-testid="include-deceased-toggle"
                        />
                    }
                    label={<Translate id="patient.status.include-deceased" />}
                />
            )}
        </Paper>
    );
};

export default SearchFiltersPanel;
